import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import UserContainer from '../containers/User';
import CarriersContainer from '../containers/logistics/Carriers';
import FMPRegionHeader from '../components/ui/FMPRegionHeader';

import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import FMPText from '../components/pureForm/FMPText';
import FormComponent from '../components/FormComponent';
import PermissionPicker from '../components/pureForm/PermissionPicker';
import Button from '@material-ui/core/Button';
import FMPCarrierMultiSelect from '../components/pureForm/FMPCarrierMultiSelect';
import AuthUserContainer from '../containers/AuthUser';

const DWP_USER_ID = 14;
const roleToDisplay = {
	employee: 'Pearson Employee',
	externalLogistics: 'External Logistics',
	externalReporting: 'External Reporting',
	mxhApiAccess: 'MHX API Access',
};

const styles = (theme) => {
	return {
		sectionHeader: {
			margin: '30px 0px 10px 0px',
			textAlign: 'left',
			textTransform: 'uppercase',
			fontSize: 14,
			color: '#777',
			fontWeight: 900,
			letterSpacing: 2,
		},
		paper: {
			width: '100%',
			overflowX: 'auto',
			textAlign: 'left',
			boxSizing: 'border-box',
			padding: 20,
			[theme.breakpoints.up('md')]: {
				padding: '10px 40px 40px 40px',
			},
		},
		addInputButton: {
			borderRadius: '100px',
			marginTop: 20,
		},
		submitButton: {
			fontSize: 18,
			borderRadius: '100px',
			padding: '20px 30px',
			textTransform: 'uppercase',
		},
		formControl: {
			width: '100%',
			margin: '10px 0',
		},
		inputGroup: {
			borderLeft: '5px solid #ccc',
			marginTop: 10,
			marginBottom: 20,
		},
		infoBox: {
			fontSize: 14,
			alignItems: 'center',
			display: 'flex',
			backgroundColor: '#f7f7f7',
			padding: '10px 15px 15px 15px',
			marginTop: 10,
			marginBottom: 5,
		},
		infoIcon: {
			color: '#2196F3',
			width: 32,
			height: 32,
			paddingRight: 5,
			verticalAlign: 'middle',
		},
	};
};

class SaveUser extends FormComponent {
	constructor(props) {
		super(props);
		const userId = _.get(this.props, ['match', 'params', 'userId']);

		this.state = {
			userId: userId ? parseInt(userId, 10) : undefined,
			isUpdate: userId,
			formFieldError: {},
			filterCarrierIds: [],
		};
	}

	componentDidMount() {
		const { user, getUser, carriers, searchCarriers } = this.props;
		const { isUpdate, userId } = this.state;

		if (!isUpdate) {
			return;
		}
		if (_.isEmpty(carriers)) {
			searchCarriers();
		}

		if (!user) {
			getUser({
				params: { userIds: [userId] },
			});
		} else {
			const newState = this.fillForm();
			this.setState(newState);
		}
	}

	componentDidUpdate(prevProps) {
		if (_.isUndefined(prevProps.user) && !_.isUndefined(this.props.user)) {
			const newState = this.fillForm();
			this.setState(newState);
		}
	}

	fillForm = () => {
		const { user } = this.props;
		if (!user) {
			return;
		}

		const newState = {};
		[
			'firstName',
			'lastName',
			'email',
			'permissions',
			'mobilePhoneNumber',
			'filterCarrierIds',
		].forEach((key) => {
			if (_.isNil(user[key])) {
				return;
			}
			newState[key] = user[key];
		});
		return newState;
	};

	submitSaveUser = () => {
		const { showErrors, addUser, updateUser } = this.props;

		const formFieldError = {};
		_.values(this.requiredPathMap).forEach(({ path, label }) => {
			if (!this.state[path]) {
				formFieldError[path] = `${label} is required`;
			}
		});

		const {
			userId,
			isUpdate,
			firstName,
			lastName,
			password,
			email,
			mobilePhoneNumber,
			filterCarrierIds,
		} = this.state;

		if (mobilePhoneNumber && !mobilePhoneNumber.match(/^[0-9]{10}$/)) {
			formFieldError.mobilePhoneNumber = 'Mobile number number should only contain 10 digits.';
		}

		//- Get all the values and do individual checks
		if (_.keys(formFieldError).length) {
			this.setState({ formFieldError });
			showErrors(_.values(formFieldError));
			return;
		}

		const permissions = [];
		_.keys(this.state.permissions).forEach((permission) => {
			if (this.state.permissions[permission]) {
				permissions.push(permission);
			}
		});

		const params = {
			firstName,
			lastName,
			email,
			mobilePhoneNumber,
			filterCarrierIds,
			permissions: _.uniq(permissions),
		};
		if (password) {
			params.password = password;
		}

		if (!isUpdate) {
			addUser({ params });
		} else {
			params.userId = userId;
			updateUser({ params });
		}
	};

	render() {
		const { classes, user, carriers } = this.props;
		const { isUpdate } = this.state;

		if (isUpdate && !user) {
			return <div>Fetching user details...</div>;
		}

		const action = isUpdate ? 'Update User ' : 'Add User';

		const isExternalLogistics = user.role === 'externalLogistics';

		return (
			<Grid container spacing={0}>
				<Grid item xs={12}>
					<Paper className={classes.paper}>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={12}>
								<FMPRegionHeader isTop={true} label={action} />
							</Grid>

							<FMPText {...this.setForm(`firstName`, 'First Name', true)} />
							<FMPText {...this.setForm(`lastName`, 'Last Name', true)} />
							<FMPText {...this.setForm(`email`, 'Email Address', true)} />

							{this.props.authUser.userId === DWP_USER_ID && (
								<FMPText {...this.setForm(`password`, 'Password', false)} />
							)}
							<FMPText {...this.setForm(`mobilePhoneNumber`, 'Mobile Number (for MFA)', true)} />

							<div style={{ padding: 10, marginTop: 10 }}>
								Role : <strong>{roleToDisplay[user.role] || 'Missing'}</strong>
							</div>

							{isExternalLogistics && (
								<FMPCarrierMultiSelect
									{...this.setForm(`filterCarrierIds`, 'Carrier Filters')}
									carriers={carriers}
									md={12}
									helpText="Controls which resources an external logistics users can see"
								/>
							)}

							<Grid item xs={12} sm={12}>
								<PermissionPicker
									{...this.setForm(`permissions`, `FMP Permissions`, true, {})}
									role={user.role}
								/>
							</Grid>

							<Grid item xs={12} sm={12}>
								<Button
									variant="contained"
									color="primary"
									className={classes.submitButton}
									onClick={this.submitSaveUser}
								>
									{action}
								</Button>
							</Grid>
						</Grid>
					</Paper>
				</Grid>
			</Grid>
		);
	}
}

SaveUser.propTypes = {
	classes: PropTypes.object.isRequired,
	addUser: PropTypes.func.isRequired,
	updateUser: PropTypes.func.isRequired,
	getUser: PropTypes.func.isRequired,
	user: PropTypes.object,
	showErrors: PropTypes.func.isRequired,
	carriers: PropTypes.array,
	searchCarriers: PropTypes.func.isRequired,
};

export default _.flow([withStyles(styles), UserContainer, CarriersContainer, AuthUserContainer])(
	SaveUser
);
